import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { VehicleTripHistory } from '@/pages/TripsPage';
import {
  ArrowLeft,
  Truck,
  Gauge,
  MapPin,
  Fuel,
  Wrench,
  Calendar,
  FileText,
  Route,
  DollarSign,
  Loader2,
  Info,
} from 'lucide-react';

interface VehicleDetail {
  id: string;
  name: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  licensePlate: string;
  color: string;
  status: 'active' | 'maintenance' | 'retired' | 'sold';
  assignedDriver: string | null;
  insuranceExpiry: string | null;
  fuelType: string;
  odometer: number;
  fuelLevel: number | null;
  lastLocation: string | null;
  nextService: string | null;
  purchaseDate: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  maintenance: 'bg-red-500/10 text-red-400 border-red-500/30',
  retired: 'bg-muted-foreground/10 text-muted-foreground border-border',
  sold: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'trips', label: 'Trips', icon: Route },
  { id: 'costs', label: 'Costs', icon: DollarSign },
  { id: 'documents', label: 'Documents', icon: FileText },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function VehicleDetailPage() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useApi<VehicleDetail>(vehicleId ? `/api/vehicles/${vehicleId}` : null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/vehicles')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Vehicles
        </button>
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-12 text-center">
          <Truck className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Vehicle not found</p>
        </div>
      </div>
    );
  }

  const v = data;

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <button onClick={() => navigate('/vehicles')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Vehicles
      </button>

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <Truck className="w-6 h-6 text-emerald-500" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{v.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[v.status] ?? STATUS_STYLES.retired}`}>
              {v.status}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{v.year} {v.make} {v.model}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Info */}
        <div className="lg:col-span-2 bg-card/50 backdrop-blur border border-border rounded-xl p-6">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">Vehicle Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Make" value={v.make} />
            <InfoRow label="Model" value={v.model} />
            <InfoRow label="Year" value={String(v.year)} />
            <InfoRow label="VIN" value={v.vin} mono />
            <InfoRow label="License Plate" value={v.licensePlate} mono />
            <InfoRow label="Color" value={v.color} />
            <InfoRow label="Assigned Driver" value={v.assignedDriver ?? 'Unassigned'} />
            <InfoRow label="Insurance Expiry" value={v.insuranceExpiry ?? '—'} />
            <InfoRow label="Fuel Type" value={v.fuelType} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Odometer */}
          <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Odometer</span>
            </div>
            <span className="text-3xl font-bold text-foreground">{v.odometer.toLocaleString()}</span>
            <span className="text-sm text-muted-foreground ml-1">miles</span>
          </div>

          {/* Mini Map Placeholder */}
          <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Location</span>
            </div>
            <div className="h-32 rounded-lg border border-border bg-muted/50 flex items-center justify-center">
              <span className="text-xs text-muted-foreground">Map preview unavailable</span>
            </div>
            {v.lastLocation && (
              <p className="text-xs text-muted-foreground mt-2">{v.lastLocation}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <OverviewCard icon={MapPin} label="Last Location" value={v.lastLocation ?? 'Unknown'} />
            <OverviewCard icon={Fuel} label="Fuel Level" value={v.fuelLevel != null ? `${v.fuelLevel}%` : '—'} />
            <OverviewCard icon={Wrench} label="Next Service" value={v.nextService ?? 'Not scheduled'} />
            <OverviewCard icon={Calendar} label="Purchase Date" value={v.purchaseDate ?? '—'} />
          </div>
        )}
        {activeTab === 'trips' && (
          <VehicleTripHistory vehicleId={vehicleId!} />
        )}
        {(activeTab === 'maintenance' || activeTab === 'costs' || activeTab === 'documents') && (
          <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-12 text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">No {activeTab} records yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
      <dd className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function OverviewCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-emerald-500" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}
